// Grant in bulk (AdminPeople.dc.html), owner and admin, with Adam's answers of 4 Oct: a second source,
// a pasted list of emails and full names (C15, answer 4), and how it was paid for with nothing picked
// (answer 2). bulk-grant checks first and writes nothing; Grant writes the batch, any new people and
// each grant in 1 transaction; Undo this batch revokes the whole batch (hub.undo_grant_batch). The
// tick list skips anyone who already has it. Nothing reads public.members: a product source is people
// with that product in the hub, and anything else comes as a pasted list. "Email each of them" sends
// inside the request, so it's for up to 100 people at a time (stage 6; bulk-grant's email_limit): over
// that, Grant waits until the box is unticked or fewer are ticked, and bulk-grant refuses it too.
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button, Checkbox, Chip, DateField, Options, GRANT_SOURCES, Select, TextArea, TextInput, useAdmin, todayInUk, addDays,
} from '../ui/index.js'
import { BULK, byBoardOrder, bulkNote, bulkRowGrants, bulkRowIsError, parseList, shortName } from './words.js'
import { cohortsOf, defaultCohort, grantable } from './data.js'

// bulk-grant's cap on "Email each of them", until a check answers with its own email_limit
export const EMAIL_LIMIT = 100

const KINDS = [
  { value: 'product', label: BULK.fromProduct },
  { value: 'email_list', label: BULK.fromList },
]

export function BulkGrant({ products, cohorts, onGranted }) {
  const { api } = useAdmin()
  const [kind, setKind] = useState('product')
  const [fromId, setFromId] = useState('')
  const [toId, setToId] = useState('')
  const [cohortId, setCohortId] = useState('')
  const [text, setText] = useState('')
  const [length, setLength] = useState('same_as_buyer')
  const [until, setUntil] = useState('')
  const [reason, setReason] = useState('')
  const [source, setSource] = useState(null)
  const [emailEach, setEmailEach] = useState(true)
  const [off, setOff] = useState(() => new Set())
  const [check, setCheck] = useState({ status: 'idle', rows: [], list: [], error: null, limit: EMAIL_LIMIT })
  const [granting, setGranting] = useState(false)
  const [error, setError] = useState(null)
  const seq = useRef(0)

  const fromOptions = useMemo(() => byBoardOrder(products.filter(p => p.access_kind === 'paid' && p.type !== 'client_space' && p.moved_in)), [products])
  const toOptions = useMemo(() => byBoardOrder(products.filter(grantable)), [products])
  const to = toOptions.find(p => p.id === toId) || null
  const toCohorts = to ? cohortsOf(cohorts, to.id) : []

  // the check, for a product source, whenever the 2 products are picked and differ
  async function runCheck(list) {
    const n = ++seq.current
    const body = { action: 'check', source_kind: kind, given_product_id: toId, length_rule: 'same_as_buyer' }
    if (cohortId) body.given_cohort_id = cohortId
    if (kind === 'product') body.source_product_id = fromId
    else body.list = list
    setCheck(c => ({ ...c, status: 'checking', error: null }))
    setOff(new Set())
    try {
      const answer = await api.bulk_grant(body)
      if (n === seq.current) setCheck({ status: 'ready', rows: answer.rows || [], list: list || [], error: null, limit: answer.email_limit || EMAIL_LIMIT })
    } catch (e) {
      if (n !== seq.current) return
      if (e.code === 'list_has_errors' && e.body?.rows) setCheck({ status: 'ready', rows: e.body.rows, list: list || [], error: null })
      else setCheck({ status: 'error', rows: [], list: list || [], error: e })
    }
  }
  useEffect(() => {
    if (kind !== 'product' || !fromId || !toId || fromId === toId) {
      if (kind === 'product') setCheck({ status: 'idle', rows: [], list: [], error: null })
      return
    }
    runCheck(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, fromId, toId, cohortId])

  function pickKind(next) {
    setKind(next)
    seq.current++
    setCheck({ status: 'idle', rows: [], list: [], error: null })
    setOff(new Set())
    setError(null)
  }
  function pickTo(p) {
    setToId(p.id)
    setCohortId(defaultCohort(p, cohorts, todayInUk()))
    if (kind === 'email_list') setCheck({ status: 'idle', rows: [], list: [], error: null })
  }
  function checkList() {
    const list = parseList(text)
    if (!list.length) { setCheck({ status: 'error', rows: [], list: [], error: { message: BULK.emptyList } }); return }
    runCheck(list)
  }

  // A row's key: a person, or a pasted line
  const keyOf = (row, i) => row.person_id ? `p:${row.person_id}:${row.line ?? i}` : `l:${row.line ?? i + 1}`
  const rows = check.rows
  const picked = rows.filter((r, i) => bulkRowGrants(r) && !off.has(keyOf(r, i)))
  const skipped = rows.filter(r => r.status === 'has_it' || r.status === 'duplicate').length
  const errors = rows.filter(bulkRowIsError).length
  const same = kind === 'product' && fromId && toId && fromId === toId
  const needDate = length === 'until_date' && !until
  // the people to be granted (the grant and new_person rows still ticked), as bulk-grant counts them
  const limit = check.limit || EMAIL_LIMIT
  const tooManyToEmail = emailEach && picked.length > limit
  const ready = check.status === 'ready' && !same && picked.length > 0 && !errors && reason.trim() !== '' && source && !needDate && !tooManyToEmail
  let hint
  if (same) hint = BULK.hintSame
  else if (errors) hint = BULK.hintErrors
  else if (check.status === 'ready' && picked.length === 0) hint = BULK.hintNobody
  else if (check.status !== 'ready') hint = ''
  else if (!reason.trim()) hint = BULK.hintReason
  else if (!source) hint = BULK.hintSource
  else if (needDate) hint = BULK.hintDate
  else if (tooManyToEmail) hint = BULK.hintEmailCap(picked.length, limit)
  else hint = skipped ? BULK.hintSkipped(skipped) : BULK.hintAll

  async function grant() {
    if (!ready || granting) return
    const body = {
      action: 'grant', source_kind: kind, given_product_id: toId, length_rule: length, source, reason: reason.trim(), email_each: emailEach,
    }
    if (cohortId) body.given_cohort_id = cohortId
    if (length === 'until_date') body.until_on = until
    if (kind === 'product') {
      body.source_product_id = fromId
      body.people = picked.map(r => r.person_id)
    } else {
      // the pasted lines still ticked, in their order (an unticked line is left out of the grant)
      const keep = new Set(rows.map((r, i) => (off.has(keyOf(r, i)) ? null : (r.line ?? i + 1))).filter(Boolean))
      body.list = check.list.filter((_, i) => keep.has(i + 1))
    }
    setGranting(true)
    setError(null)
    try {
      const answer = await api.bulk_grant(body)
      setGranting(false)
      onGranted?.({ answer, product: to, emailEach })
    } catch (e) {
      setGranting(false)
      if (e.code === 'list_has_errors' && e.body?.rows) setCheck(c => ({ ...c, rows: e.body.rows }))
      setError(e)
    }
  }

  return (
    <section className="apl-bulk" aria-labelledby="apl-bulk-h">
      <div className="apl-bulk-head">
        <h2 className="apl-bulk-title" id="apl-bulk-h">{BULK.title}</h2>
        <span className="apl-hint">{BULK.line}</span>
      </div>
      <div className="apl-source">
        <span className="apl-step-label" id="apl-bulk-kind">{BULK.fromKind}</span>
        <Options labelledBy="apl-bulk-kind" row className="apl-compact" value={kind} onChange={pickKind} options={KINDS} />
      </div>
      <div className="apl-grid2">
        {kind === 'product' ? (
          <div className="apl-step">
            <span className="apl-step-label" id="apl-bulk-from">{BULK.from}</span>
            <div className="ad-chips" role="group" aria-label={BULK.from}>
              {fromOptions.map(p => <Chip key={p.id} product={p} pressed={p.id === fromId} onClick={() => setFromId(p.id)}>{shortName(p)}</Chip>)}
            </div>
          </div>
        ) : (
          <div className="apl-step">
            <label className="apl-step-label" htmlFor="apl-bulk-list">{BULK.paste}</label>
            <TextArea id="apl-bulk-list" rows={5} value={text} onChange={v => { setText(v); if (check.status !== 'idle') setCheck({ status: 'idle', rows: [], list: [], error: null }) }}
              placeholder={BULK.pastePlaceholder} aria-describedby="apl-bulk-list-hint" />
            <span className="apl-hint" id="apl-bulk-list-hint">{BULK.pasteHint}</span>
            <span><Button size="sm" onClick={checkList} disabled={!toId || !text.trim()} busy={check.status === 'checking'} busyLabel={BULK.checking}>{BULK.checkList}</Button></span>
          </div>
        )}
        <div className="apl-step">
          <span className="apl-step-label">{BULK.gets}</span>
          <div className="ad-chips" role="group" aria-label={BULK.gets}>
            {toOptions.map(p => <Chip key={p.id} product={p} pressed={p.id === toId} onClick={() => pickTo(p)}>{shortName(p)}</Chip>)}
          </div>
          {toCohorts.length > 0 && (
            <label className="apl-inline-field">
              <span className="apl-inline-label">{BULK.cohort}</span>
              <Select value={cohortId} onChange={setCohortId} placeholder={BULK.noCohort} options={toCohorts.map(c => ({ value: c.id, label: c.name }))} />
            </label>
          )}
        </div>
      </div>
      <div className="apl-step">
        <span className="apl-step-label">{BULK.who}</span>
        <div className="apl-ticklist" role="group" aria-label={BULK.who}>
          {check.status === 'checking' && <p className="apl-tick-empty" role="status">{BULK.checking}.</p>}
          {check.status === 'error' && <p className="apl-tick-empty" role="alert">{check.error?.message}</p>}
          {check.status === 'ready' && rows.length === 0 && <p className="apl-tick-empty">{kind === 'product' ? BULK.nobody : BULK.emptyList}</p>}
          {check.status === 'ready' && rows.map((r, i) => {
            const k = keyOf(r, i)
            const can = bulkRowGrants(r)
            const bad = bulkRowIsError(r)
            const name = r.full_name || r.email || `Line ${r.line ?? i + 1}`
            return (
              <div key={k} className={`apl-tick${can ? '' : ' apl-tick--skipped'}${bad ? ' apl-tick--wrong' : ''}`}>
                <Checkbox checked={can && !off.has(k)} disabled={!can} aria-label={BULK.tickLabel(name)}
                  onChange={on => setOff(prev => { const next = new Set(prev); if (on) next.delete(k); else next.add(k); return next })}>
                  <span className="apl-tick-name">{name}</span>
                </Checkbox>
                <span className="apl-tick-note">{bad && <span className="apl-tick-flag">{BULK.flag} </span>}{bulkNote(r)}</span>
              </div>
            )
          })}
        </div>
      </div>
      <div className="apl-grid-len">
        <div className="apl-step">
          <span className="apl-step-label" id="apl-bulk-len">{BULK.length}</span>
          <Options labelledBy="apl-bulk-len" row className="apl-compact" value={length} onChange={setLength} options={BULK.lengths} />
          {length === 'until_date' && <DateField label={BULK.until} value={until} onChange={setUntil} min={addDays(todayInUk(), 1)} />}
        </div>
        <TextInput aria-label="Reason" value={reason} onChange={setReason} placeholder={BULK.reason} maxLength={1000} />
      </div>
      <div className="apl-source">
        <span className="apl-step-label" id="apl-bulk-source">{BULK.source}</span>
        <Options labelledBy="apl-bulk-source" row className="apl-compact" value={source} onChange={setSource} options={GRANT_SOURCES} />
      </div>
      <div className="apl-bulk-foot">
        <span className="apl-email">
          <Checkbox checked={emailEach} onChange={setEmailEach} aria-describedby="apl-bulk-email-note">{BULK.email}</Checkbox>
          <span className="apl-hint" id="apl-bulk-email-note">{BULK.emailLimit(limit)}</span>
        </span>
        <span className="apl-actions">
          <span className="apl-hint" aria-live="polite">{hint}</span>
          <Button kind="ink" onClick={grant} disabled={!ready} busy={granting} busyLabel={BULK.busy}>{BULK.button(picked.length)}</Button>
        </span>
      </div>
      {error && <p className="ad-error" role="alert">{error.code === 'list_has_errors' ? BULK.hintErrors : error.message}</p>}
    </section>
  )
}
